import { Context } from 'react';

import { FolderId } from 'modules/domains/foldering';
import { GraphQLTypeNameData, GRAPHQL_TYPENAMES } from 'modules/graphql/types';

export enum SelectionTypes {
  Content = 'Content',
  Folder = 'Folder',
}

export type FolderableContent<IdType = number, NameType = string> = {
  id: IdType;
  name: NameType;
};

export type FolderableItem<
  ContentId = number,
  ContentName = string,
> = GraphQLTypeNameData<GRAPHQL_TYPENAMES> & FolderableContent<ContentId, ContentName>;

export type FolderableFolder = FolderableItem<FolderId, string>;

type CurrentSelectionContent<ContentId = number, ContentName = string> = {
  node: GraphQLTypeNameData<GRAPHQL_TYPENAMES>;
  id: ContentId;
  name: ContentName;
  type: SelectionTypes.Content;
};

type CurrentSelectionFolder = {
  node: GraphQLTypeNameData<GRAPHQL_TYPENAMES>;
  id: FolderId;
  name: string;
  type: SelectionTypes.Folder;
};

export type CurrentSelection<ContentId = number, ContentName = string> =
  | CurrentSelectionContent<ContentId, ContentName>
  | CurrentSelectionFolder;

export interface SelectionContextState<ContentId = number, ContentName = string> {
  // Unstaged selections (used for showing the bulk action bar)
  bulk: CurrentSelection<ContentId, ContentName>[];
  // Select/Unselect a single item
  toggleBulkSelection: (item: FolderableItem<ContentId, ContentName> | FolderableFolder) => void;
  // Add all visible items to the unstaged selection
  addPageToBulk: () => void;
  // Remove all visible items to the unstaged selection
  removePageFromBulk: () => void;
  // Staged selections (used for the actionable modals)
  active: CurrentSelection<ContentId, ContentName>[];
  // Add a single item to the staged selection
  selectItem: (item: FolderableItem<ContentId, ContentName> | FolderableFolder) => void;
  // Stage all unstaged selections
  selectBulk: () => void;
  // Clears both active/staged AND bulk/unstaged selections
  clearAll: () => void;
}

export type CreateContextResponse<ContentId = number, ContentName = string> = [
  Context<SelectionContextState<ContentId, ContentName>>,
  () => SelectionContextState<ContentId, ContentName>,
];

export interface SelectionParams<ContentId = number, ContentName = string> {
  folderId: FolderId;
  folders: FolderableFolder[];
  contentItems: FolderableItem<ContentId, ContentName>[];
}
